import { Add, Edit, Search } from '@mui/icons-material';
import {
  Button,
  Chip,
  FormControl,
  Input,
  InputAdornment,
  InputLabel,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Loading } from 'src/components/Loading';
import { SCRoutes } from 'src/config';
import { UserOnAgency, UserSimpleInfo } from 'src/types';
import { useDashboardFetchAllUsers } from '../hooks';

type Props = {
  testID?: string;
};

export const DashboardUsersHome: React.FC<Props> = ({ testID = 'DashboardUsersHome' }) => {
  const { t } = useTranslation();

  const [searchTerm, setSearchTerm] = useState('');

  const { fetchAllUsers, isLoading, users } = useDashboardFetchAllUsers();

  const [filteredUsers, setSearchedUsers] = useState<UserSimpleInfo[]>(users);

  useEffect(() => {
    if (!searchTerm) {
      setSearchedUsers(users);
      return;
    }

    if (!searchTerm?.length) {
      return;
    }

    const filtered = users.filter((user) => {
      return (
        user.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user?.city?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user?.tel?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user?.role?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t(user?.role)?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user?.agencies?.some((agency) => agency?.agency?.name?.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    });

    setSearchedUsers(filtered);
  }, [searchTerm, users, t]);

  const handleFilter = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event?.target) {
      return;
    }

    setSearchTerm(event.target.value as string);
  }, []);

  useEffect(() => {
    fetchAllUsers();
  }, [fetchAllUsers]);

  const getAffectedAgenciesNames = useCallback((agencies: UserOnAgency[]) => {
    return (
      <Stack direction="row" spacing={1}>
        {agencies
          .map((agency) => agency.agency.name)
          .map((name) => (
            <Chip key={name} label={name} variant="outlined" color="info" size="small" />
          ))}
      </Stack>
    );
  }, []);

  if (isLoading) {
    return <Loading />;
  }

  return (
    <div className="mt-1 pl-2 px-2">
      <h1 className="text-2xl font-bold">{t('dashboard-users.users-list-page-title')}</h1>
      <p className="text-sm text-gray-500">{t('dashboard-users.users-list-sub-title')}</p>

      <div className="mt-2">
        <div className=" bg-white rounded-sm w-1/2 m-auto mb-2 px-2 py-1">
          <FormControl variant="standard" className="w-full m-1">
            <InputLabel htmlFor="input-with-icon-adornment">{t('dashboard-users.search-title')}</InputLabel>
            <Input
              id="input-with-icon-adornment"
              placeholder={t('dashboard-users.search-placeholder')}
              value={searchTerm}
              onChange={handleFilter}
              startAdornment={
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              }
            />
          </FormControl>
        </div>

        <div className="flex justify-end px-2 my-2">
          <Button variant="contained" startIcon={<Add />} component={Link} to={SCRoutes.dashboard.users.new}>
            {t('dashboard-users.create-new-user')}
          </Button>
        </div>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('dashboard-users.user-name')}</TableCell>
                <TableCell>{t('dashboard-users.user-role')}</TableCell>
                <TableCell>{t('dashboard-users.user-email')}</TableCell>
                <TableCell>{t('dashboard-users.user-tel')}</TableCell>
                <TableCell>{t('dashboard-users.user-status')}</TableCell>
                <TableCell>{t('dashboard-users.user-agencies')}</TableCell>
                <TableCell>{t('dashboard-users.user-actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsers.map((user) => (
                <TableRow key={user.id} className={user.status === 'DISABLED' ? 'bg-red-300' : ''}>
                  <TableCell>{`${user.firstName} ${user.lastName} `}</TableCell>
                  <TableCell>{t(user.role)}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>{user.tel}</TableCell>
                  <TableCell>{t(user?.status as string)}</TableCell>
                  <TableCell>{getAffectedAgenciesNames(user.agencies)}</TableCell>
                  <TableCell>
                    <Button
                      variant="text"
                      color="primary"
                      startIcon={<Edit />}
                      component={Link}
                      to={`${SCRoutes.dashboard.users.edit.replace(':id', user.id)}`}
                    >
                      {t('dashboard-users.user-edit')}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
    </div>
  );
};
